import React, { memo, useEffect, useMemo, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Icon } from '../icons/Icon';
import { reactGaEvent } from '../../utils/cookie-consent/react-ga-safe';
import { Link } from '../Link';
import { fixMojibake } from '../../utils/text/fix-mojibake';
import { trackJobClick } from '../../utils/analytics/analytics';
import { ApplicationStateSelector } from '../ApplicationStateSelector';
import { LazyTooltip } from '../Tooltip';
import { MatchDonutWithLoginPrompt } from '../MatchDonut';
import { useHitShared } from './hooks/useHitShared';
import { ApplicationStateMenu } from '../application-state';
import { useUserJobStateSync } from '../../hooks/useUserJobStateSync';
import { trackJobApply } from '../../utils/analytics/analytics';
import { companyProfileUrlPath } from '../../utils/routing/company-profile-url-path';
import { HitCompanyColumn } from './HitCompanyColumn';
import { HitCompanyInsightsCueLink } from './HitCompanyInsightsCueLink';
import { getSinglePrioritizedLocation } from '../location/utils/locationUtils';
import { isDark } from './utils/brandColorUtils';
import {
  COMPACT_TITLE_LINK_CLASS_MOBILE,
  COMPACT_TITLE_SUBTITLE_CLASS_MOBILE,
} from './compactTitleClasses';
import type { HitProps } from './types';
import { calculateFreshness } from '../dates/utils/freshnessCalculation';
import {
  STATE_BUTTON_COLOR_CLASSES,
  STATE_ICON_COLOR_CLASSES,
  getHitStateButtonSurfaceClass,
  getHitStateIconClass,
} from '../application-state/state-classes';
import { formatSrpSalaryDisplay } from '../../utils/data/format-job-card-labels';
import { SalaryInferenceExplainer } from '../SalaryInferenceExplainer';
import { resolveSalaryInferenceExplanationKind } from '../../utils/job-detail/resolve-salary-inference-explanation-kind';
import { formatSalaryInferenceExplanation } from '../../utils/job-detail/salary-inference-explanation-intl';
import { shouldLinkCompanyProfile } from '../../utils/company/should-link-company-profile';
import { normalizeToStringArray } from '../../utils/data/normalize-to-string-array';

/**
 * Compact variant of the Hit component
 * Displays job information in a horizontal, compact layout
 */
export const HitCompact: React.FC<HitProps> = memo(({ hit, sendEvent, index }) => {
  const intl = useIntl();
  const shared = useHitShared({ hit, sendEvent });
  const { setAppliedJobState, syncApplicationStateToBackend } = useUserJobStateSync();
  const menuButtonRef = React.useRef<HTMLButtonElement>(null);
  const [isNavigatingToDetails, setIsNavigatingToDetails] = useState(false);

  const {
    jobId,
    jobUrl,
    articleId,
    titleId,
    jobTitle,
    isSaved,
    isApplied,
    isHidden,
    currentApplicationState,
    isToolsPage,
    savedJobPayload,
    brandColour,
    useBrandColour,
    leftBorderStyle,
    matchScore,
    matchScoreData,
    matchScoreMeta,
    isAuthenticated,
    onLoginClick,
    actions,
    getUrlForIndexUiState,
    pushIndexUiState,
    makeRefinementState,
    showToast,
    browsedJobRef,
    menuRefMobile,
    menuOpenSource,
    setMenuOpenSource,
    isMenuOpen,
    setIsMenuOpen,
    buttonState,
    locationData,
    filters,
  } = shared;

  const requiredLanguageTags = useMemo(() => normalizeToStringArray(hit.requiredLanguages), [hit.requiredLanguages]);

  // Track view event - use hit.objectID instead of hit object to avoid unnecessary re-runs
  const hitId = hit.objectID || hit.id;
  useEffect(() => {
    if (sendEvent) {
      sendEvent('view', hit, 'Job Viewed (CA English)');
    }
  }, [sendEvent, hitId]);

  // Memoize filter parsing to avoid JSON.parse on every render
  const parsedFilters = useMemo(() => {
    if (typeof filters === 'string') {
      try {
        return JSON.parse(filters) as Record<string, string[]>;
      } catch {
        return {};
      }
    }
    return (filters || {}) as Record<string, string[]>;
  }, [filters]);

  // Memoize expensive location calculations
  const userLocationFilter = useMemo(() => parsedFilters.location, [parsedFilters.location]);
  const singleLocation = useMemo(
    () => getSinglePrioritizedLocation(hit, userLocationFilter),
    [hit, userLocationFilter]
  );
  const detailsLoadingLabel = intl.formatMessage({
    id: 'hit.detailsLoading',
    defaultMessage: 'Loading...',
    description: 'Shown in job card while job detail page is loading',
  });
  const salaryInferenceKind = useMemo(
    () =>
      resolveSalaryInferenceExplanationKind({
        salaryIsEstimate: hit.salaryIsEstimate,
        salaryRangeMin: hit.salaryRangeMin,
        salaryRangeMax: hit.salaryRangeMax,
        salaryDetails: hit.salaryDetails,
      }),
    [hit.salaryIsEstimate, hit.salaryRangeMin, hit.salaryRangeMax, hit.salaryDetails],
  );
  const salaryInferenceText = useMemo(
    () => formatSalaryInferenceExplanation(salaryInferenceKind, intl.formatMessage),
    [salaryInferenceKind, intl],
  );

  const companyDisplayName = hit.companyName ?? hit.companyId ?? '';
  const canLinkCompanyProfile = shouldLinkCompanyProfile({
    companyId: hit.companyId,
    isTyomarkkinatori: hit.isTyomarkkinatori,
  });
  const companyProfileHref = useMemo(
    () =>
      canLinkCompanyProfile && hit.companyId
        ? companyProfileUrlPath(intl.locale as 'en' | 'fi' | 'sv', hit.companyId)
        : '',
    [canLinkCompanyProfile, hit.companyId, intl.locale],
  );
  const companyProfileLabels = useMemo(
    () => ({
      aria: intl.formatMessage(
        {
          id: 'hit.companyProfile.aria',
          defaultMessage: 'View company profile, hiring data, and insights for {company}',
          description: 'Accessibility label for the employer logo and name link on a job card',
        },
        { company: companyDisplayName },
      ),
      title: intl.formatMessage(
        {
          id: 'hit.companyLinkTitle',
          defaultMessage: 'Open company profile — data and insights for {company}',
          description: 'Tooltip for the employer logo and name link on a job card',
        },
        { company: companyDisplayName },
      ),
      cue: intl.formatMessage({
        id: 'hit.companyInsights.cue',
        defaultMessage: 'Company insights',
        description:
          'Short label under the employer name on a job card; the block links to the company profile',
      }),
    }),
    [intl, companyDisplayName],
  );

  const handleJobDetailsClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    includeGaEvent: boolean = false
  ) => {
    const isPrimaryClick = e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
    if (isPrimaryClick) {
      setIsNavigatingToDetails(true);
    }

    if (sendEvent) {
      sendEvent('click', hit, 'Job Details Clicked');
      sendEvent('conversion', hit, 'Job Details Clicked');
    }

    if (includeGaEvent) {
      reactGaEvent({
        category: 'interactions',
        action: 'click_see_ad',
        label: hit.objectID,
        nonInteraction: false,
      });
    }
  };

  return (
    <article
      id={articleId}
      ref={browsedJobRef}
      className={`job-listing hit-card ${isMenuOpen ? 'hit-card--menu-open' : ''} ${isNavigatingToDetails ? 'hit-card--navigating' : ''}`}
      title={jobTitle}
      aria-labelledby={titleId}
      style={{
        ...leftBorderStyle,
        position: 'relative',
      }}
    >
      {isNavigatingToDetails ? (
        <div className="hit-card__loading-indicator" role="status" aria-live="polite">
          <span className="hit-card__loading-spinner" aria-hidden />
          <span className="sr-only">{detailsLoadingLabel}</span>
        </div>
      ) : null}

      <HitCompanyColumn
        companyProfileUrl={companyProfileHref || undefined}
        companyId={hit.companyId ?? ''}
        companyNameDisplay={(hit as { companyNameDisplay?: string }).companyNameDisplay ?? hit.companyName ?? hit.companyId}
        registryCompanyFormDescription={hit.registryCompanyFormDescription}
        isTyomarkkinatori={hit.isTyomarkkinatori}
        index={index ?? 0}
        locale={intl.locale}
        ariaLabel={companyProfileLabels.aria}
        title={companyProfileLabels.title}
        insightsCue={companyProfileLabels.cue}
      />

      <div className="hit-card__main">
        <div className="hit-card__mobile-col">
          <div className="hit-card__title-block">
            <h3 id={titleId} className="sr-only">{jobTitle}</h3>
            <Link
              href={jobUrl}
              className={`${COMPACT_TITLE_LINK_CLASS_MOBILE} mb-0.5`}
              onClick={(e) => {
                handleJobDetailsClick(e);
                trackJobClick({
                  jobId: hit.objectID,
                  jobTitle: hit.normalisedTitle || hit.title || '',
                  companyId: hit.companyId || '',
                  locale: intl.locale,
                });
              }}
            >
              {fixMojibake(hit.normalisedTitle ?? '')}
            </Link>
            {hit.title && hit.title !== hit.normalisedTitle ? (
              <div className={COMPACT_TITLE_SUBTITLE_CLASS_MOBILE}>
                {fixMojibake(hit.title)}
              </div>
            ) : null}
            {companyProfileHref && canLinkCompanyProfile ? (
              <HitCompanyInsightsCueLink
                variant="under-title"
                href={companyProfileHref}
                locale={intl.locale}
                title={companyProfileLabels.title}
                insightsCue={companyProfileLabels.cue}
              />
            ) : null}
            {isToolsPage && (
              <div className="mt-2">
                <ApplicationStateSelector
                  jobId={jobId}
                  currentState={currentApplicationState}
                  markAsAppliedOnStateSet={true}
                  jobData={savedJobPayload}
                  setAppliedJobState={setAppliedJobState}
                  syncApplicationStateToBackend={syncApplicationStateToBackend}
                  isApplied={isApplied}
                  trackJobApply={trackJobApply}
                  locale={intl.locale}
                />
              </div>
            )}
          </div>

          <div className="hit-card__meta">
          {singleLocation.display ? (
            <div className="hit-pills-grid">
              <LazyTooltip
                content={intl.formatMessage({ id: 'hit.tooltip.filterByLocation', defaultMessage: 'Filter jobs in: {locations}', description: 'Tooltip text for location link that filters search results to show only jobs in the specified location(s)' }, { locations: singleLocation.display })}
                position="top"
              >
                <Link
                  href={getUrlForIndexUiState(makeRefinementState('location', singleLocation.locationForUrl))}
                  className="hit-pill-mobile hit-pill-mobile--filter-location hit-pill-mobile--interactive"
                  title={singleLocation.fullList}
                  onClick={(e) => { e.preventDefault(); if (singleLocation.locationForUrl) pushIndexUiState(makeRefinementState('location', singleLocation.locationForUrl)); }}
                >
                  <Icon name="mapPin" useMaskForColor className="hit-icon-pill-mobile" />
                  <span className="hit-pill__center">{fixMojibake(singleLocation.display)}</span>
                </Link>
              </LazyTooltip>
              {hit.remoteness && hit.remoteness.trim() !== '' && (
                <LazyTooltip
                  content={intl.formatMessage({ id: 'hit.tooltip.filterByRemoteness', defaultMessage: 'Filter jobs by remoteness: {remoteness}', description: 'Tooltip text for remoteness link that filters search results to show only jobs with the specified work model (remote, hybrid, onsite)' }, { remoteness: hit.remoteness })}
                  position="top"
                >
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('remoteness', hit.remoteness ?? ''))}
                    className="hit-pill-mobile hit-pill-mobile--filter-location hit-pill-mobile--interactive hit-pill-mobile--w-auto"
                    onClick={(e) => { e.preventDefault(); pushIndexUiState(makeRefinementState('remoteness', hit.remoteness ?? '')); }}
                  >
                    <Icon name="wifi" useMaskForColor className="hit-icon-pill-mobile" />
                    <span className="hit-pill__center">{fixMojibake(hit.remoteness ?? '')}</span>
                  </Link>
                </LazyTooltip>
              )}
              {hit.jobType && hit.jobType.trim() !== '' && (
                <LazyTooltip
                  content={intl.formatMessage({ id: 'hit.tooltip.filterByJobType', defaultMessage: 'Filter jobs by type: {jobType}', description: 'Tooltip text for job type link that filters search results to show only jobs of the specified type (full-time, part-time, etc.)' }, { jobType: hit.jobType })}
                  position="top"
                >
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('jobType', hit.jobType ?? ''))}
                    className="hit-pill-mobile hit-pill-mobile--filter-job hit-pill-mobile--interactive hit-pill-mobile--w-auto"
                    onClick={(e) => { e.preventDefault(); pushIndexUiState(makeRefinementState('jobType', hit.jobType ?? '')); }}
                  >
                    <Icon name="briefcase" useMaskForColor className="hit-icon-pill-mobile" />
                    <span className="hit-pill__center">{fixMojibake(hit.jobType ?? '')}</span>
                  </Link>
                </LazyTooltip>
              )}
              {hit.normalisedRoleLevel && hit.normalisedRoleLevel.trim() !== '' && (
                <LazyTooltip
                  content={intl.formatMessage({ id: 'hit.tooltip.filterByLevel', defaultMessage: 'Filter jobs by level: {level}', description: 'Tooltip text for role level link that filters search results to show only jobs at the specified experience level (junior, mid, senior, etc.)' }, { level: hit.normalisedRoleLevel })}
                  position="top"
                >
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('normalisedRoleLevel', hit.normalisedRoleLevel ?? ''))}
                    className="hit-pill-mobile--filter-job-md-only hit-pill-mobile--interactive cursor-pointer"
                    onClick={(e) => { e.preventDefault(); pushIndexUiState(makeRefinementState('normalisedRoleLevel', hit.normalisedRoleLevel ?? '')); }}
                  >
                    <span className="hit-pill__center">
                      {fixMojibake(hit.normalisedRoleLevel ?? '')}
                    </span>
                  </Link>
                </LazyTooltip>
              )}
              {requiredLanguageTags.length > 0 && (
                <LazyTooltip
                  content={intl.formatMessage(
                    { id: 'hit.tooltip.filterByLanguages', defaultMessage: 'Filter jobs by required languages: {languages}', description: 'Tooltip text for languages link that filters search results to show only jobs requiring the specified languages' },
                    { languages: requiredLanguageTags.join(', ') }
                  )}
                  position="top"
                >
                  <div className="hit-pill-mobile hit-pill-mobile--filter-job hit-pill-mobile--w-auto">
                    <Icon name="globeAlt" useMaskForColor className="hit-icon-pill-mobile" />
                    <span className="hit-pill__center">
                      {requiredLanguageTags.slice(0, 2).map((lang: string, idx: number) => (
                        <span key={lang + idx}>
                          {fixMojibake(lang)}
                          {idx < Math.min(requiredLanguageTags.length, 2) - 1 && ', '}
                        </span>
                      ))}
                      {requiredLanguageTags.length > 2 && ` +${requiredLanguageTags.length - 2}`}
                    </span>
                  </div>
                </LazyTooltip>
              )}
              <span className="hit-pill-mobile hit-pill-mobile--filter-salary">
                <SalaryInferenceExplainer explanationText={salaryInferenceText}>
                  {hit._salaryLabel
                    ? hit._salaryLabel
                    : typeof hit.salaryRangeMin === 'number' && typeof hit.salaryRangeMax === 'number' && hit.salaryRangeMin > 0 && hit.salaryRangeMax > 0
                    ? formatSrpSalaryDisplay({
                        salaryRangeMin: hit.salaryRangeMin,
                        salaryRangeMax: hit.salaryRangeMax,
                        salaryIsEstimate: hit.salaryIsEstimate,
                        locale: intl.locale,
                      })
                    : intl.formatMessage({ id: 'hit.salaryNotSpecified.card', defaultMessage: 'Salary not specified', description: 'Shown when salary is not specified in job card' })}
                </SalaryInferenceExplainer>
              </span>
              {(hit._lastSeenLabel || hit.lastSeen) && (
                  hit._lastSeenLabel ? (
                  <span className="hit-pill-mobile hit-pill-mobile--gray-muted">
                    <Icon name="clock" useMaskForColor className="hit-icon-pill-mobile" />
                    {hit._lastSeenLabel}
                  </span>
                ) : (() => {
                  const freshness = calculateFreshness(hit.lastSeen as string | number | undefined);
                  if (!freshness) return null;
                  return (
                    <span className="hit-pill-mobile hit-pill-mobile--gray-muted">
                      <Icon name="clock" useMaskForColor className="hit-icon-pill-mobile" />
                      <FormattedMessage
                        id={freshness.id}
                        defaultMessage={
                          freshness.id === 'hit.lastSeen.minutesAgo'
                            ? '{value, plural, one {{value} minute ago} other {{value} minutes ago}}'
                            : freshness.id === 'hit.lastSeen.hoursAgo'
                            ? '{value, plural, one {{value} hour ago} other {{value} hours ago}}'
                            : '{value, plural, one {{value} day ago} other {{value} days ago}}'
                        }
                        values={{ value: freshness.value }}
                        description="Indicates how recently the job was last seen/crawled."
                      />
                    </span>
                  );
                })()
              )}
            </div>
          ) : (
            <div className="hit-pills-grid">
              {hit.remoteness && hit.remoteness.trim() !== '' && (
                <LazyTooltip
                  content={intl.formatMessage({ id: 'hit.tooltip.filterByRemoteness', defaultMessage: 'Filter jobs by remoteness: {remoteness}', description: 'Tooltip text for remoteness link that filters search results to show only jobs with the specified work model (remote, hybrid, onsite)' }, { remoteness: hit.remoteness })}
                  position="top"
                >
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('remoteness', hit.remoteness ?? ''))}
                    className="hit-pill-mobile hit-pill-mobile--filter-location hit-pill-mobile--interactive hit-pill-mobile--w-auto"
                    onClick={(e) => { e.preventDefault(); pushIndexUiState(makeRefinementState('remoteness', hit.remoteness ?? '')); }}
                  >
                    <Icon name="wifi" useMaskForColor className="hit-icon-pill-mobile" />
                    <span className="hit-pill__center">{fixMojibake(hit.remoteness ?? '')}</span>
                  </Link>
                </LazyTooltip>
              )}
              {hit.jobType && hit.jobType.trim() !== '' && (
                <LazyTooltip
                  content={intl.formatMessage({ id: 'hit.tooltip.filterByJobType', defaultMessage: 'Filter jobs by type: {jobType}', description: 'Tooltip text for job type link that filters search results to show only jobs of the specified type (full-time, part-time, etc.)' }, { jobType: hit.jobType })}
                  position="top"
                >
                  <Link
                    href={getUrlForIndexUiState(makeRefinementState('jobType', hit.jobType ?? ''))}
                    className="hit-pill-mobile hit-pill-mobile--filter-job hit-pill-mobile--interactive hit-pill-mobile--w-auto"
                    onClick={(e) => { e.preventDefault(); pushIndexUiState(makeRefinementState('jobType', hit.jobType ?? '')); }}
                  >
                    <Icon name="briefcase" useMaskForColor className="hit-icon-pill-mobile" />
                    <span className="hit-pill__center">{fixMojibake(hit.jobType ?? '')}</span>
                  </Link>
                </LazyTooltip>
              )}
            </div>
          )}

          <div className="hit-card__actions-mobile" ref={menuRefMobile}>
            <MatchDonutWithLoginPrompt
              score={matchScore}
              size={40}
              onLoginClick={onLoginClick}
              breakdown={matchScoreData ?? undefined}
              className="hit-card__donut-mobile"
              isAuthenticated={isAuthenticated}
              matchScoreMeta={matchScoreMeta}
            />
            <div className="hit-card__actions-mobile-inner">
              <LazyTooltip
                content={isSaved
                  ? intl.formatMessage({ id: 'hit.tooltip.saved', defaultMessage: 'Remove from saved jobs', description: 'Tooltip text for save button when job is already saved, indicating clicking will remove it from saved jobs' })
                  : intl.formatMessage({ id: 'hit.tooltip.save', defaultMessage: 'Save this job to compare with others', description: 'Tooltip text for save button when job is not saved, indicating clicking will save the job' })}
                position="top"
              >
                <button
                  className={`hit-btn-save ${isSaved ? '' : 'hit-btn-save--unsaved hover:bg-[#F2EADF] dark:hover:bg-[#342415]'} transition cursor-pointer`}
                  aria-label={
                    isSaved
                      ? intl.formatMessage({ id: 'hit.saved', defaultMessage: 'Saved', description: 'Button label when job is saved' })
                      : intl.formatMessage({ id: 'hit.save', defaultMessage: 'Save', description: 'Button label to save a job' })
                  }
                  style={isSaved ? {
                    backgroundColor: useBrandColour && brandColour ? brandColour : '#C8DDCE',
                    borderColor: useBrandColour && brandColour ? brandColour : '#C8DDCE',
                  } : {}}
                  onClick={actions.handleToggleSaved}
                  type="button"
                >
                  {isSaved ? (
                    <Icon name="bookmark" variant="solid" useMaskForColor className="hit-icon-btn" style={{ color: useBrandColour && brandColour ? (isDark(brandColour) ? '#fff' : '#1E1A14') : '#1E1A14' }} />
                  ) : (
                    <Icon name="bookmark" useMaskForColor className="hit-icon-btn hit-icon-btn--muted" />
                  )}
                </button>
              </LazyTooltip>

              <LazyTooltip
                  content={buttonState
                    ? buttonState.label
                    : intl.formatMessage({ id: 'hit.tooltip.moreOptions', defaultMessage: 'More options' })}
                  position="top"
                  showOnFocus={false}
                >
                  <button
                    ref={menuButtonRef}
                    className={`hit-btn-menu border-0 ${buttonState ? 'hit-btn-menu--with-state w-auto px-1.5 md:px-2' : 'hit-btn-menu--compact'} ${
                      buttonState ? getHitStateButtonSurfaceClass(buttonState.color) : STATE_BUTTON_COLOR_CLASSES.default
                    } hover:bg-jc-elevated-hover transition cursor-pointer`}
                    aria-label={buttonState
                      ? buttonState.label
                      : intl.formatMessage({ id: 'hit.moreOptions', defaultMessage: 'More options' })}
                    onClick={() => setMenuOpenSource((prev) => (prev ? null : 'mobile'))}
                    type="button"
                  >
                    {buttonState ? (
                      <>
                        <Icon
                          name={buttonState.icon}
                          variant="solid"
                          useMaskForColor
                          className={`hit-icon-btn ${getHitStateIconClass(buttonState.color)}`}
                        />
                        <Icon name="ellipsisVertical" useMaskForColor className={`hit-icon-btn ${STATE_ICON_COLOR_CLASSES.default}`} />
                      </>
                    ) : (
                      <Icon name="ellipsisVertical" useMaskForColor className={`hit-icon-btn ${STATE_ICON_COLOR_CLASSES.default}`} />
                    )}
                  </button>
                </LazyTooltip>
              <LazyTooltip
                content={intl.formatMessage({ id: 'hit.tooltip.details', defaultMessage: 'View full job details and description', description: 'Tooltip text for button/link that opens the full job detail page with complete job description' })}
                position="top"
              >
                <Link
                  className={`hit-btn-details-mobile ${isNavigatingToDetails ? 'opacity-80 pointer-events-none' : ''}`}
                  style={{
                    backgroundColor: useBrandColour && brandColour ? brandColour : '#C8DDCE',
                    color: useBrandColour && brandColour ? (isDark(brandColour) ? '#fff' : '#1E1A14') : '#1E1A14',
                    border: 'none',
                  }}
                  href={jobUrl}
                  onClick={(e) => handleJobDetailsClick(e, true)}
                  aria-busy={isNavigatingToDetails}
                >
                  {intl.formatMessage({ id: 'hit.seeAd', defaultMessage: 'Details', description: 'Button label to view job details' })}
                </Link>
              </LazyTooltip>
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Single dropdown instance – positioned by the button that opened it (desktop or mobile) */}
      {menuOpenSource && (
        <ApplicationStateMenu
          currentApplicationState={currentApplicationState}
          isApplied={isApplied}
          isHidden={isHidden}
          onSetApplicationState={actions.handleSetApplicationState}
          onToggleApplied={actions.handleToggleApplied}
          onToggleHide={actions.handleToggleHide}
          onClose={() => setMenuOpenSource(null)}
          className="w-56"
          triggerRef={menuButtonRef}
          usePortal={true}
        />
      )}
    </article>
  );
});

HitCompact.displayName = 'HitCompact';
